import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useIsFocused, useNavigation, useRoute } from "@react-navigation/native";
import i18n from "utils/i18n";
import { StyleSheet, View } from "react-native";
import _ from 'lodash';
import { dataConstants } from "services/redux/data/constants";
import { colorList } from "utils/colors";
import { AppRoutes } from "navigations/app-routes";
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import ThemedLayout from "components/atoms/ThemedLayout";
import LoadingComponent from 'components/atoms/Loading'
import Qrcode_Presence from 'utils/qrcode/Presence'
import HelperAlerts from 'utils/HelperAlerts'
import Qrcode_Authentication from 'utils/qrcode/Authentication'
import { useEmployeeScanStatus } from 'utils/objects/employee-scan'
import ObjectBooking from 'utils/objects/booking'
import ThemedCamera, { cameraFacing } from 'components/atoms/ThemedCamera'
import { routeBookingConstants, routeBookingConstantValues } from 'utils/constants/route/RouteBookingConstants'
import Qrcode_MOD from 'utils/qrcode/MOD'
import { MODE_SCAN_OUT, qrcodeConstants } from 'utils/constants/qrcode-constants'
import selectBookings from 'services/redux/data/selectors/selectBookings'
import { generateSelectTimeslotGroupsData } from 'utils/hooks/use-select-timeslot-groups'
import { settingsOptions } from 'utils/constants/settings-constants'
import HelperValidate from 'utils/helper-validate'
import FetchCachedMyBooking from 'utils/fetch/cached/FetchCachedMyBooking'
import HelperDate, { dateFormat } from 'utils/helper-date'
import HomeQrCodeValidateBookingModal from 'scenes/home/qrcode/components/ValidateBookingModal'
import HelperJson from 'utils/HelperJson'
import ObjectEmployee from 'utils/objects/employee'

const QRCODE_BUILDING_PREFIX = 'building';
const QRCODE_FLOOR_PREFIX = 'floor';
const QRCODE_ROOM_PREFIX = 'room';

const QRCODE_MOD_PREFIX = 'mod';

const QRCODE_VALIDATE_BOOKING = 'validate_booking';

const QRCODE_AUTHENTICATION_PREFIX = 'authentication';

const HomeQrCodeScanScene = () => {
	const isFocused = useIsFocused();

	const navigation = useNavigation();
	const route = useRoute();

	const _employee = useSelector( state => {
		return state.data[dataConstants.EMPLOYEE];
	} );
	const _bookings = useSelector( selectBookings );

	const [ scanned, setScanned ] = useState( false );
	const [ scanMode ] = useState( _.get( route.params, 'scanMode', null ) );

	const bookings = ObjectBooking.filterAccepted( _.filter( _bookings, { 'booking_date': HelperDate.date().format( dateFormat.DATE ) } ) );

	const { isScanningEnabled, scanMode: scanModeNonRoute } = useEmployeeScanStatus();

	// Get the scan mode :)
	const _scanMode = scanMode !== null ? scanMode : scanModeNonRoute;

	const [ employeeScanInData, setEmployeeScanInData ] = useState( null );
	
	const closeEmployeeScanInModal = () => {
		setEmployeeScanInData( null );
		setScanned( false );
	}

	useEffect( () => {
		if ( isFocused ) {
			setScanned( false );
		}
	}, [ isFocused ] );

	const handleScanError = ( error_label ) => {
		// Check for errors
		if( _.isObject( error_label ) ) {
			error_label = error_label.message ?? '';
		}

		if( error_label === undefined ) {
			HelperAlerts.alert(
				i18n.t('page.scan.scanned.error_title'),
				i18n.t('page.scan.scanned.error'),
				[
					{
						text: i18n.t('basic.ok'),
						onPress : () => {
							setScanned( false )
						}
					}
				]
			);
		} else {
			HelperAlerts.alert(
				i18n.t(`page.scan.scanned.error_${error_label}_title`),
				i18n.t(`page.scan.scanned.error_${error_label}`),
				[
					{
						text: i18n.t('basic.ok'),
						onPress : () => {
							setScanned( false )
						}
					}
				]
			);
		}
	}

	const handlePresenceScanSuccess = ( employeeScan ) => {
		const building = _.get( employeeScan, 'building', {} );
		const timeslotGroup = _.get( employeeScan, 'timeslot_group', {} );
		const timeslotKind = _.get( employeeScan, 'timeslot_kind', {} );
		
		navigation.replace( AppRoutes.PRESENCE_TRANSPORT, {
			navigatedFromScan: true,
			scanMode: _scanMode,
			message: i18n.t( `notification.scan_success.${ _scanMode }` ),
			buildingGuid: _.get( building, 'guid', null ),
			timeslotGroupGuid: _.get( timeslotGroup, 'guid', null ),
			timeslotKindGuid: _.get( timeslotKind, 'guid', null ),
		});
	}

	const handleAuthenicationSuccess = () => {
		navigation.popToTop();
	}

	const handleScan = ({ type, data }, callback ) => {
		callback();
		
		if( scanned || employeeScanInData !== null ) {
			return;
		}
		
		setScanned( true );

		let action = null;
		if( HelperJson.isJson( data ) ){
			action = _.get( JSON.parse( data ), 'action', null );
		} else {
			action = data.split(':')[0];
		}
		
		switch( action ) {
			case QRCODE_AUTHENTICATION_PREFIX:
				Qrcode_Authentication.scan( data, handleAuthenicationSuccess, handleScanError.bind( this, 'authentication_error' ) );
				return;

			case QRCODE_BUILDING_PREFIX:
			case QRCODE_FLOOR_PREFIX:
			case QRCODE_ROOM_PREFIX:
				Qrcode_MOD.scanOld( data, handleQrcodeScanSuccess, handleScanError );
				break;

			case QRCODE_MOD_PREFIX:
				Qrcode_MOD.scan( data.split(':')[1], handleQrcodeScanSuccess, handleScanError );
				break;

			case QRCODE_VALIDATE_BOOKING:
				if( _.get( _employee, 'can_scan_presence_qr_code', settingsOptions.NO ) === settingsOptions.YES || ObjectEmployee.isOndmand( _employee ) ) {
					//navigation.navigate( AppRoutes.QRCODE_VALIDATE_BOOKING, { data: JSON.parse( data ) } );
					setEmployeeScanInData( JSON.parse( data ) );
					
					setScanned( false );
				}else{
					handleScanError( 'no_rights_to_scan_presence_qr_code' );
				}
				break;

			default:
				setScanned( false );
				break;
		}
	};

	const handleQrcodeScanSuccess = ( qrcode ) => {
		// Parse the data
		const data = JSON.parse( qrcode.data );

		// Check if the scan has a qr code
		const qrCodeHasBooking = HelperValidate.isValid( qrcode?.booking )
		if( qrCodeHasBooking ) {
			FetchCachedMyBooking.reload();
		}

		// Set that we scanned
		setScanned( true );

		switch( data.scanOption ) {
			case qrcodeConstants.SCANIN:
				if( isScanningEnabled && ( qrCodeHasBooking || _.size( bookings ) > 0) ) {
					const qrcode = [];

					// Set the location guid
					switch( true ) {
						case HelperValidate.isValid( data.room ):
							qrcode.push( `room:${data.room}` );
							break;

						case HelperValidate.isValid( data.floor ):
							qrcode.push( `floor:${data.floor}` );
							break;

						case HelperValidate.isValid( data.building ):
							qrcode.push( `building:${data.building}` );
							break;
					}

					// Check for timeslot group if it is set in bookings
					if( HelperValidate.isValid( data.timeslotGroup ) ) {
						qrcode.push( `timeslotGroup:${data.timeslotGroup}` );
					}

					// Check for timeslot group if it is set in bookings
					if( HelperValidate.isValid( data.timeslotKind ) ) {
						qrcode.push( `timeslotKind:${ _.values( data.timeslotKind ).join('~') }` );
					}

					// Combine the array to the code
					const code = qrcode.join('|');

					switch( _scanMode ) {
						case MODE_SCAN_OUT:
							Qrcode_Presence.scanOut( code, handlePresenceScanSuccess, handleScanError );
							break;

						default:
							Qrcode_Presence.scanIn( code, handlePresenceScanSuccess, handleScanError );
							break;
					}
				} else {
					handleScanError( 'no_booking' );
				}
				break;

			case qrcodeConstants.BOOK:
				let params = {
					[ routeBookingConstants.THROUGH_QR_SCAN ]: routeBookingConstantValues[ routeBookingConstants.THROUGH_QR_SCAN ].YES
				};

				if( data.room ){
					params[ routeBookingConstants.FORCED_ROOM_GUID ] = data.room;
				}
				if( data.floor ){
					params[ routeBookingConstants.FORCED_FLOOR_GUID ] = data.floor;
				}
				if( data.building ){
					params[ routeBookingConstants.FORCED_BUILDING_GUID ] = data.building;
				}
				if( data.timeslotGroup ){
					params[ routeBookingConstants.FORCED_TIMESLOT_GROUP_GUID ] = data.timeslotGroup;
				}

				if( data.timeslotKind ) {
					let timeslot_kind_nr = 1;
					_.forEach( data.timeslotKind, ( timeslotKind ) => {
						params[ routeBookingConstants[ `FORCED_TIMESLOT_${timeslot_kind_nr}`]] = timeslotKind;
						timeslot_kind_nr++;
					});
				}

				let routeLocation = AppRoutes.TAB_BOOK;

				if( data.previewTimeslots ) {
					if( data.building ){
						params[ routeBookingConstants.BUILDING ] = data.building;
					}
					if( data.floor ){
						params[ routeBookingConstants.FLOOR ] = data.floor;
					}
					if( data.room ){
						params[ routeBookingConstants.ROOM ] = data.room;
					}
					if( data.timeslotGroup ){
						params[ routeBookingConstants.TIMESLOT_GROUP ] = data.timeslotGroup;
					}
					routeLocation = AppRoutes.ADD_BOOKING_PREVIEW;
				}

				// check if we can actually follow above path
				const timeslotGroups = generateSelectTimeslotGroupsData( params );

				let accessError = false;
				if( data.building && _.get( timeslotGroups, [ data.timeslotGroup ], null ) === null ){
					accessError = true;
				}
				if( data.building && _.get( timeslotGroups, [ data.timeslotGroup, 'buildingTree', data.building ], null ) === null ){
					accessError = true;
				}

				if( data.floor && _.get( timeslotGroups, [ data.timeslotGroup, 'buildingTree', data.building, 'floors', data.floor ], null ) === null ){
					accessError = true;
				}
				if( data.room && _.get( timeslotGroups, [ data.timeslotGroup, 'buildingTree', data.building, 'floors', data.floor, 'rooms', data.room ], null ) === null ){
					accessError = true;
				}

				if( accessError ) {
					handleScanError( 'insufficient_access' );
				}else{
					navigation.replace( routeLocation, params);
				}
				break;
		}
	}

	return (
		<ThemedSafeAreaComponentTop
			top={ false }>
			<ThemedLayout
				title={ i18n.t('page.scan.title') }
				enableScrollView={ false }
				topNavigation={ true }
				showBackButton={ true }>
				
				<View
					style={ styles.scanContainer }>

					{ isFocused && (
						<ThemedCamera
							onBarCodeScanned={ scanned ? undefined : handleScan }
							style={ styles.scan }
							facing={ cameraFacing.BACK }
						/>
					)}
					
					{ employeeScanInData !== null && (
						<HomeQrCodeValidateBookingModal
							visible={ true }
							setVisible={ closeEmployeeScanInModal }
							data={ employeeScanInData }
						/>
					)}
					
					{ scanned && (
						<LoadingComponent fullscreen={ true } size={ 'l' } />
					)}
				</View>
			</ThemedLayout>
		</ThemedSafeAreaComponentTop>
	)
}

const styles = StyleSheet.create({
	scanContainer: {
		flex: 1,
		flexDirection: 'row',
		justifyContent: 'center',
	},
	scan: {
		flex: 1,
		width: '100%',
		height: '100%',
		borderWidth: 1
	},
	backdrop: {
		backgroundColor: 'rgba(0, 0, 0, 0.5)',
	},

	buttonContainer: {
		width: '100%',
		flexDirection: 'row',
		height: 50
	},
	buttonHolder: {
		flex: 1,
		width: '50%',
		padding: 2
	},
	button:{
		backgroundColor: colorList.WHITE.primary
	},
	buttonActive:{
		backgroundColor: colorList.PRIMARY.primary
	},
	buttonInactive:{
		color: colorList.SUBTITLE.p
	}
});

export default HomeQrCodeScanScene
